import React, { useState, useRef, useEffect } from "react";
import {
  ClassicPuzzleState,
  EMPTY_TILE,
  isAdjacentToEmpty
} from "../../game/classicPuzzleEngine";

interface ClassicPuzzleViewProps {
  state: ClassicPuzzleState;
  isShowingSolution: boolean;
  completedAt: number | null;
  onSwap: (tilePos: { row: number; col: number }) => void;
}

export const ClassicPuzzleView: React.FC<ClassicPuzzleViewProps> = ({
  state,
  isShowingSolution,
  completedAt,
  onSwap
}) => {
  if (!state || !state.grid || !state.dimensions) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "rgba(148, 163, 184, 0.8)" }}>
        Invalid puzzle state
      </div>
    );
  }

  const { grid, dimensions } = state;
  const [draggedPiece, setDraggedPiece] = useState<{ row: number; col: number } | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const size = dimensions.rows;

  // Test image from Unsplash: "black wooden window on brown brick wall" by Christopher Ott
  const baseUrl = "https://unsplash.com/photos/gt3qT1h86bM/download?force=true&w=800";

  // Create solved grid for comparison (with empty tile at the end)
  const solvedGrid: (number | typeof EMPTY_TILE)[][] = [];
  {
    let id = 0;
    const totalTiles = dimensions.rows * dimensions.cols;
    for (let r = 0; r < dimensions.rows; r++) {
      const row: (number | typeof EMPTY_TILE)[] = [];
      for (let c = 0; c < dimensions.cols; c++) {
        if (id === totalTiles - 1) {
          row.push(EMPTY_TILE);
        } else {
          row.push(id++);
        }
      }
      solvedGrid.push(row);
    }
  }

  const getPieceStyle = (
    tileId: number | typeof EMPTY_TILE,
    row: number,
    col: number
  ): React.CSSProperties => {
    const isDragging = draggedPiece?.row === row && draggedPiece?.col === col;
    let isAdjacent = false;
    try {
      isAdjacent = !completedAt && !isShowingSolution && isAdjacentToEmpty(state, row, col);
    } catch (e) {
      // Ignore errors in adjacency check
    }
    const isCorrect =
      !isShowingSolution && tileId !== EMPTY_TILE && solvedGrid[row][col] === tileId;

    // Empty tile style
    if (tileId === EMPTY_TILE) {
      return {
        width: "100%",
        aspectRatio: "1 / 1",
        backgroundColor: "rgba(15, 23, 42, 0.8)",
        borderRadius: 2,
        border: "1px solid rgba(148, 163, 184, 0.3)",
        cursor: "default"
      };
    }

    // Regular tile style
    const solvedRow = Math.floor(tileId / dimensions.cols);
    const solvedCol = tileId % dimensions.cols;
    const bgPosX = (solvedCol / (dimensions.cols - 1 || 1)) * 100;
    const bgPosY = (solvedRow / (dimensions.rows - 1 || 1)) * 100;

    return {
      width: "100%",
      aspectRatio: "1 / 1",
      backgroundImage: `url(${baseUrl})`,
      backgroundSize: `${size * 100}% ${size * 100}%`,
      backgroundPosition: `${bgPosX}% ${bgPosY}%`,
      backgroundRepeat: "no-repeat",
      borderRadius: 2,
      border: isCorrect
        ? "2px solid rgba(34, 197, 94, 0.6)"
        : isAdjacent
          ? "2px solid rgba(59, 130, 246, 0.6)"
          : "1px solid rgba(15, 23, 42, 0.75)",
      cursor: completedAt ? "default" : isAdjacent ? "grab" : "not-allowed",
      opacity: isDragging ? 0.5 : 1,
      transition: isDragging ? "none" : "border-color 0.2s ease",
      touchAction: "none"
    };
  };

  const getSlotPosition = (clientX: number, clientY: number): { row: number; col: number } | null => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const col = Math.floor((x / rect.width) * dimensions.cols);
    const row = Math.floor((y / rect.height) * dimensions.rows);
    if (row >= 0 && row < dimensions.rows && col >= 0 && col < dimensions.cols) {
      return { row, col };
    }
    return null;
  };

  const handlePointerDown = (e: React.PointerEvent, row: number, col: number) => {
    if (completedAt || isShowingSolution) return;
    const tile = grid[row][col];
    if (tile === EMPTY_TILE) return; // Can't drag empty tile
    try {
      if (!isAdjacentToEmpty(state, row, col)) return; // Can only drag adjacent tiles
    } catch (err) {
      return; // If check fails, don't allow drag
    }
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    setDraggedPiece({ row, col });
  };

  useEffect(() => {
    if (!draggedPiece) return;

    const handlePointerMove = (e: PointerEvent) => {
      e.preventDefault();
    };

    const handlePointerUp = (e: PointerEvent) => {
      // Just swap the dragged piece with empty (engine handles adjacency check)
      if (draggedPiece) {
        onSwap(draggedPiece);
      }
      setDraggedPiece(null);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: false });
    window.addEventListener("pointerup", handlePointerUp, { passive: true });
    window.addEventListener("pointercancel", handlePointerUp, { passive: true });

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [draggedPiece, state, dimensions.cols, dimensions.rows, onSwap]);

  const sourceGrid = isShowingSolution ? solvedGrid : grid;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 420,
        marginInline: "auto"
      }}
    >
      <div
        style={{
          position: "relative",
          borderRadius: 16,
          padding: 4,
          background: "linear-gradient(135deg, #e5e7eb, #9ca3af, #111827)"
        }}
      >
        <div
          ref={boardRef}
          style={{
            backgroundColor: "#020617",
            borderRadius: 12,
            padding: 4
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${dimensions.cols}, 1fr)`,
              gap: 2
            }}
          >
            {sourceGrid.map((row, r) =>
              row.map((tileId, c) => {
                const actualTileId = isShowingSolution ? solvedGrid[r][c] : grid[r][c];
                return (
                  <div
                    key={`${r}-${c}`}
                    style={getPieceStyle(actualTileId, r, c)}
                    onPointerDown={(e) => handlePointerDown(e, r, c)}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

